import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription, interval } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { PermitV2Service } from '../../services/permits.abstract.service';
import { AdminPermitDetail, PermitFile, PermitFileAttachment, permitFileName, PermitMessage, PopulatedUser } from '../../models/permit.model';
import { UserService } from '../../../user/service/user.abstract.service';
import { ProfileResponse } from '../../../user/user.model';

interface PendingAttachment {
    url: string;
    type: 'image' | 'pdf' | 'other';
    name?: string;
}

@Component({
    selector: 'app-permit-messages',
    templateUrl: './permit-messages.component.html',
    styleUrls: ['./permit-messages.component.scss'],
})
export class PermitMessagesComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('messagesContainer') messagesContainer: ElementRef;

    @Input() permit: AdminPermitDetail;
    @Input() active = false;

    @Output() messagesRead = new EventEmitter<void>();
    @Output() permitUpdated = new EventEmitter<void>();

    messageForm: FormGroup;
    sending = false;
    uploading = false;
    pendingAttachments: PendingAttachment[] = [];

    private destroy$ = new Subject<void>();
    private pollSub: Subscription | null = null;

    // Messages only carry a raw sender id (see PermitMessage.sender) — resolved profiles are
    // cached here by id so avatars/names render without refetching on every change-detection pass.
    private senderProfiles = new Map<string, ProfileResponse>();
    private fetchedSenderIds = new Set<string>();

    constructor(
        private readonly fb: FormBuilder,
        private readonly permitService: PermitV2Service,
        private readonly userService: UserService,
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.resolveMessageSenders();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.permit) {
            this.resolveMessageSenders();
        }
        if (changes.active) {
            const isNowActive: boolean = changes.active.currentValue;
            if (isNowActive) {
                this.onActivated();
            } else {
                this.stopPolling();
            }
        }
    }

    ngOnDestroy(): void {
        this.stopPolling();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initForm(): void {
        this.messageForm = this.fb.group({
            message: ['', [Validators.required]],
        });
    }

    private onActivated(): void {
        if (this.permit) {
            this.permitService
                .markMessagesAsRead(this.permit._id)
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe(() => {
                    this.messagesRead.emit();
                });
        }
        this.startPolling();
    }

    private startPolling(): void {
        this.stopPolling();
        this.pollSub = interval(15000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                if (this.permit) {
                    const prevCount = this.permit.messages?.length ?? 0;
                    this.permitService
                        .getPermitById(this.permit._id)
                        .pipe(take(1), takeUntil(this.destroy$))
                        .subscribe((updated) => {
                            if (updated) {
                                this.permit = updated;
                                this.resolveMessageSenders();
                                const newCount = updated.messages?.length ?? 0;
                                if (newCount > prevCount) {
                                    setTimeout(() => this.scrollToBottom(), 50);
                                }
                            }
                        });
                }
            });
    }

    private stopPolling(): void {
        if (this.pollSub) {
            this.pollSub.unsubscribe();
            this.pollSub = null;
        }
    }

    // ─── Messages helpers ─────────────────────────────────────────────────────

    get sortedMessages(): PermitMessage[] {
        if (!this.permit?.messages) {
            return [];
        }
        return [...this.permit.messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }

    isAgentMessage(message: PermitMessage): boolean {
        return message?.senderType === 'agent';
    }

    /** Fetch (once) the profile behind each unpopulated sender id so avatars/names can render. */
    private resolveMessageSenders(): void {
        const ids = Array.from(
            new Set((this.permit?.messages || []).map((m) => (typeof m.sender === 'string' ? m.sender : null)).filter((id): id is string => !!id && !this.fetchedSenderIds.has(id))),
        );
        ids.forEach((id) => {
            this.fetchedSenderIds.add(id);
            this.userService
                .getProfileById(id)
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe({
                    next: (profile) => {
                        if (profile) this.senderProfiles.set(id, profile);
                    },
                    // Allow retry on transient failures (e.g. next poll) while still avoiding refetch-on-success.
                    error: () => this.fetchedSenderIds.delete(id),
                });
        });
    }

    private resolvedSender(message: PermitMessage): { firstName: string; lastName: string; picture?: string } | null {
        if (message.sender && typeof message.sender === 'object') {
            const user = message.sender as PopulatedUser;
            return { firstName: user.firstName, lastName: user.lastName };
        }
        if (typeof message.sender === 'string') {
            return this.senderProfiles.get(message.sender) || null;
        }
        return null;
    }

    getSenderName(message: PermitMessage): string {
        const sender = this.resolvedSender(message);
        return sender ? `${sender.firstName || ''} ${sender.lastName || ''}`.trim() : '';
    }

    getSenderPicture(message: PermitMessage): string | null {
        return this.resolvedSender(message)?.picture || null;
    }

    getInitials(message: PermitMessage): string {
        const sender = this.resolvedSender(message);
        if (!sender) {
            return '?';
        }
        const first = sender.firstName?.charAt(0)?.toUpperCase() || '';
        const last = sender.lastName?.charAt(0)?.toUpperCase() || '';
        return `${first}${last}` || '?';
    }

    // ─── File helpers ─────────────────────────────────────────────────────────

    isImage(url: string): boolean {
        return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url);
    }

    isPdf(url: string): boolean {
        return /\.pdf(\?|$)/i.test(url);
    }

    private getFileType(url: string): 'image' | 'pdf' | 'other' {
        if (this.isImage(url)) return 'image';
        if (this.isPdf(url)) return 'pdf';
        return 'other';
    }

    getMessageFiles(message: PermitMessage): PermitFile[] {
        return message.files || [];
    }

    /** Label for an attachment — icons alone give the agent no way to tell two files apart. */
    fileName(file: PermitFile): string {
        return permitFileName(file);
    }

    // ─── Send ────────────────────────────────────────────────────────────────

    /**
     * Attached files go out as { url, originalName } so the name the citizen sees is the name the
     * agent picked — the upload endpoint only hands back a generated blob URL. With nothing
     * attached the endpoint still wants its legacy [''] sentinel.
     */
    get attachmentPayload(): (string | PermitFileAttachment)[] {
        if (!this.pendingAttachments.length) {
            return [''];
        }
        return this.pendingAttachments.map((a) => ({ url: a.url, originalName: a.name || '' }));
    }

    sendMessage(): void {
        if (this.messageForm.invalid || this.sending) {
            return;
        }
        const messageText: string = this.messageForm.get('message')?.value?.trim();
        if (!messageText) {
            return;
        }

        this.sending = true;
        const attachments = this.attachmentPayload;

        this.permitService
            .sendMessage(this.permit._id, { message: messageText, attachments })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.messageForm.reset();
                    this.pendingAttachments = [];
                    this.sending = false;
                    this.permitUpdated.emit();
                    setTimeout(() => this.scrollToBottom(), 100);
                },
                error: () => {
                    this.sending = false;
                },
            });
    }

    // ─── File upload ──────────────────────────────────────────────────────────

    onNativeFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }
        Array.from(input.files).forEach((file) => {
            this.uploadFile(file);
        });
        input.value = '';
    }

    private uploadFile(file: File): void {
        this.uploading = true;
        this.permitService
            .uploadTempFile(file)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (urls: string[]) => {
                    if (urls?.length > 0) {
                        urls.forEach((url) => {
                            this.pendingAttachments.push({
                                url,
                                type: this.getFileType(url),
                                name: file.name,
                            });
                        });
                    }
                    this.uploading = false;
                },
                error: () => {
                    this.uploading = false;
                },
            });
    }

    removeAttachment(index: number): void {
        this.pendingAttachments.splice(index, 1);
    }

    // ─── Scroll ───────────────────────────────────────────────────────────────

    private scrollToBottom(): void {
        if (this.messagesContainer?.nativeElement) {
            const el = this.messagesContainer.nativeElement;
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        }
    }
}
